import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import DesktopLayout from '../layouts/DesktopLayout';
import { formatShortDate } from '../utils/dateFormatter';
import '../styles/EventManagement.css';

/**
 * Event Management Page
 * Admin interface for managing local events that impact occupancy and pricing
 * Role-based access: All authenticated users can view, only admins can create/edit/delete
 */
const EventManagement = () => {
  const { user } = useContext(AuthContext);
  // Navigation state
  const [activeNavItem, setActiveNavItem] = useState('events');

  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is admin (only admins can create/edit/delete events)
  const isAdmin = user?.role === 'admin';
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    startDate: '',
    endDate: '',
    category: 'other',
    city: 'jeddah', // Default city
    impactRadius: 5,
    expectedOccupancyIncrease: 20,
    priceMultiplier: 1.2,
    location: {
      name: 'Jeddah',
      nameAr: 'جدة',
      coordinates: {
        type: 'Point',
        coordinates: [39.1925, 21.4858] // Jeddah default
      },
      address: '',
      addressAr: ''
    },
    isActive: true
  });
  const [filter, setFilter] = useState('active');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [filter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {};
      if (filter === 'active') params.active = 'true';
      if (filter === 'inactive') params.active = 'false';

      const response = await axios.get('/api/v1/events', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setEvents(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('فشل تحميل الفعاليات');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/events/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      if (locationField === 'lng' || locationField === 'lat') {
        const newCoords = [...formData.location.coordinates.coordinates];
        if (locationField === 'lng') newCoords[0] = parseFloat(value);
        if (locationField === 'lat') newCoords[1] = parseFloat(value);
        setFormData({
          ...formData,
          location: {
            ...formData.location,
            coordinates: {
              ...formData.location.coordinates,
              coordinates: newCoords
            }
          }
        });
      } else {
        setFormData({
          ...formData,
          location: {
            ...formData.location,
            [locationField]: value
          }
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (editingEvent) {
        // Update existing event
        await axios.put(`/api/v1/events/${editingEvent._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new event
        await axios.post('/api/v1/events', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
      fetchStats();
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.response?.data?.message || 'فشل حفظ الفعالية');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);

    // Derive city from location name if not stored
    let city = event.city || 'jeddah';
    if (event.location && event.location.nameAr) {
      const cityMap = {
        'جدة': 'jeddah',
        'الرياض': 'riyadh',
        'مكة المكرمة': 'mecca',
        'المدينة المنورة': 'medina',
        'الدمام': 'dammam',
        'الطائف': 'taif',
        'الخبر': 'khobar',
        'أبها': 'abha'
      };
      city = cityMap[event.location.nameAr] || 'jeddah';
    }

    setFormData({
      name: event.name,
      nameAr: event.nameAr,
      description: event.description || '',
      descriptionAr: event.descriptionAr || '',
      startDate: event.startDate.split('T')[0],
      endDate: event.endDate.split('T')[0],
      category: event.category,
      city: city,
      impactRadius: event.impactRadius,
      expectedOccupancyIncrease: event.expectedOccupancyIncrease,
      priceMultiplier: event.priceMultiplier,
      location: event.location,
      isActive: event.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الفعالية؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v1/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents();
      fetchStats();
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('فشل حذف الفعالية');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nameAr: '',
      description: '',
      descriptionAr: '',
      startDate: '',
      endDate: '',
      category: 'other',
      city: 'jeddah', // Default city
      impactRadius: 5,
      expectedOccupancyIncrease: 20,
      priceMultiplier: 1.2,
      location: {
        name: 'Jeddah',
        nameAr: 'جدة',
        coordinates: {
          type: 'Point',
          coordinates: [39.1925, 21.4858]
        },
        address: '',
        addressAr: ''
      },
      isActive: true
    });
  };

  const formatDate = formatShortDate;

  const getCategoryLabel = (category) => {
    const labels = {
      religious: 'ديني',
      sports: 'رياضي',
      entertainment: 'ترفيهي',
      business: 'أعمال',
      cultural: 'ثقافي',
      festival: 'مهرجان',
      other: 'أخرى'
    };
    return labels[category] || category;
  };

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="إدارة الفعاليات المحلية"
    >
      <div className="event-management-page" dir="rtl">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>
              <i className="bi bi-calendar-event"></i>
              إدارة الفعاليات المحلية
            </h1>
            <p className="header-subtitle">
              إدارة الفعاليات التي تؤثر على الإشغال والأسعار
            </p>
          </div>
          {isAdmin && (
            <button
              className="btn-add-event"
              onClick={() => {
                setShowForm(!showForm);
                setEditingEvent(null);
                resetForm();
              }}
            >
              <i className="bi bi-plus-circle"></i>
              إضافة فعالية جديدة
            </button>
          )}
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="bi bi-calendar-check"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>إجمالي الفعاليات</p>
            </div>
          </div>
          <div className="stat-card active">
            <div className="stat-icon">
              <i className="bi bi-calendar-event"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.active}</h3>
              <p>فعاليات نشطة</p>
            </div>
          </div>
          <div className="stat-card upcoming">
            <div className="stat-icon">
              <i className="bi bi-calendar3"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.upcoming}</h3>
              <p>قادمة (30 يوم)</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-circle"></i>
          {error}
        </div>
      )}

      {/* Event Form - Admin Only */}
      {isAdmin && showForm && (
        <div className="event-form-card">
          <div className="form-header">
            <h3>
              <i className="bi bi-calendar-plus"></i>
              {editingEvent ? 'تعديل الفعالية' : 'إضافة فعالية جديدة'}
            </h3>
            <button
              className="btn-close"
              onClick={() => {
                setShowForm(false);
                setEditingEvent(null);
                resetForm();
              }}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* Basic Info - Arabic First */}
              <div className="form-group">
                <label>اسم الفعالية *</label>
                <input
                  type="text"
                  name="nameAr"
                  value={formData.nameAr}
                  onChange={handleInputChange}
                  required
                  placeholder="اسم الفعالية"
                />
              </div>

              <div className="form-group">
                <label>اسم الفعالية (English - اختياري)</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Event Name (Optional)"
                />
              </div>

              {/* Dates */}
              <div className="form-group">
                <label>تاريخ البداية *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>تاريخ النهاية *</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Category */}
              <div className="form-group">
                <label>الفئة *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="religious">ديني</option>
                  <option value="sports">رياضي</option>
                  <option value="entertainment">ترفيهي</option>
                  <option value="business">أعمال</option>
                  <option value="cultural">ثقافي</option>
                  <option value="festival">مهرجان</option>
                  <option value="other">أخرى</option>
                </select>
              </div>

              {/* Impact Settings */}
              <div className="form-group">
                <label>نطاق التأثير (كم)</label>
                <input
                  type="number"
                  name="impactRadius"
                  value={formData.impactRadius}
                  onChange={handleInputChange}
                  min="1"
                  max="50"
                />
              </div>

              <div className="form-group">
                <label>زيادة الإشغال المتوقعة (%)</label>
                <input
                  type="number"
                  name="expectedOccupancyIncrease"
                  value={formData.expectedOccupancyIncrease}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label>مضاعف السعر المقترح</label>
                <input
                  type="number"
                  name="priceMultiplier"
                  value={formData.priceMultiplier}
                  onChange={handleInputChange}
                  min="1"
                  max="3"
                  step="0.1"
                />
              </div>

              {/* Location - Simplified City Selection */}
              <div className="form-group">
                <label>المدينة *</label>
                <select
                  name="city"
                  value={formData.city || ''}
                  onChange={(e) => {
                    const selectedCity = e.target.value;
                    const cityCoordinates = {
                      'jeddah': { lng: 39.1925, lat: 21.4858, nameAr: 'جدة', name: 'Jeddah' },
                      'riyadh': { lng: 46.6753, lat: 24.7136, nameAr: 'الرياض', name: 'Riyadh' },
                      'mecca': { lng: 39.8262, lat: 21.4225, nameAr: 'مكة المكرمة', name: 'Mecca' },
                      'medina': { lng: 39.6142, lat: 24.5247, nameAr: 'المدينة المنورة', name: 'Medina' },
                      'dammam': { lng: 50.0888, lat: 26.3927, nameAr: 'الدمام', name: 'Dammam' },
                      'taif': { lng: 40.4157, lat: 21.2703, nameAr: 'الطائف', name: 'Taif' },
                      'khobar': { lng: 50.2084, lat: 26.2172, nameAr: 'الخبر', name: 'Al Khobar' },
                      'abha': { lng: 42.5053, lat: 18.2164, nameAr: 'أبها', name: 'Abha' }
                    };

                    if (selectedCity && cityCoordinates[selectedCity]) {
                      const coords = cityCoordinates[selectedCity];
                      setFormData({
                        ...formData,
                        city: selectedCity,
                        location: {
                          ...formData.location,
                          name: coords.name,
                          nameAr: coords.nameAr,
                          coordinates: {
                            type: 'Point',
                            coordinates: [coords.lng, coords.lat]
                          }
                        }
                      });
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  required
                >
                  <option value="">اختر المدينة</option>
                  <option value="jeddah">جدة</option>
                  <option value="riyadh">الرياض</option>
                  <option value="mecca">مكة المكرمة</option>
                  <option value="medina">المدينة المنورة</option>
                  <option value="dammam">الدمام</option>
                  <option value="taif">الطائف</option>
                  <option value="khobar">الخبر</option>
                  <option value="abha">أبها</option>
                </select>
              </div>

              <div className="form-group">
                <label>اسم الموقع المحدد (اختياري)</label>
                <input
                  type="text"
                  name="location.nameAr"
                  value={formData.location.nameAr}
                  onChange={handleInputChange}
                  placeholder="مثال: استاد الملك عبدالله"
                />
              </div>

              {/* Descriptions - Arabic First */}
              <div className="form-group full-width">
                <label>الوصف</label>
                <textarea
                  name="descriptionAr"
                  value={formData.descriptionAr}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="وصف الفعالية"
                />
              </div>

              <div className="form-group full-width">
                <label>الوصف (English - اختياري)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Event Description (Optional)"
                />
              </div>

              {/* Active Status */}
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  فعالية نشطة
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="submit" className="btn-save" disabled={loading}>
                <i className="bi bi-check-circle"></i>
                {loading ? 'جاري الحفظ...' : editingEvent ? 'تحديث' : 'حفظ'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowForm(false);
                  setEditingEvent(null);
                  resetForm();
                }}
              >
                <i className="bi bi-x-circle"></i>
                إلغاء
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            الكل
          </button>
          <button
            className={filter === 'active' ? 'active' : ''}
            onClick={() => setFilter('active')}
          >
            نشطة
          </button>
          <button
            className={filter === 'inactive' ? 'active' : ''}
            onClick={() => setFilter('inactive')}
          >
            غير نشطة
          </button>
        </div>
      </div>

      {/* Events List */}
      {loading && !showForm ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      ) : (
        <div className="events-grid">
          {events.length === 0 ? (
            <div className="no-events">
              <i className="bi bi-calendar-x"></i>
              <p>لا توجد فعاليات</p>
            </div>
          ) : (
            events.map(event => (
              <div key={event._id} className={`event-card ${!event.isActive ? 'inactive' : ''}`}>
                <div className="event-header">
                  <div className="event-title">
                    <h3>{event.nameAr || event.name}</h3>
                    <span className={`event-category ${event.category}`}>
                      {getCategoryLabel(event.category)}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="event-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEdit(event)}
                        title="تعديل"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => handleDelete(event._id)}
                        title="حذف"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="event-details">
                  <div className="detail-row">
                    <i className="bi bi-calendar-range"></i>
                    <span>{formatDate(event.startDate)} - {formatDate(event.endDate)}</span>
                  </div>
                  <div className="detail-row">
                    <i className="bi bi-geo-alt"></i>
                    <span>{event.location?.nameAr || event.location?.name || 'غير محدد'}</span>
                  </div>
                  <div className="detail-row">
                    <i className="bi bi-bullseye"></i>
                    <span>نطاق التأثير: {event.impactRadius} كم</span>
                  </div>
                  <div className="detail-row">
                    <i className="bi bi-graph-up-arrow"></i>
                    <span>زيادة متوقعة: {event.expectedOccupancyIncrease}%</span>
                  </div>
                </div>

                {!event.isActive && (
                  <div className="inactive-badge">
                    غير نشطة
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
      </div>
    </DesktopLayout>
  );
};

export default EventManagement;
